const STORAGE_PREFIX = 'falcon-planner:gantt-grid-split:';

export type GanttGridSplit = {
  version: 1;
  /** Grid panel width as % of the gantt root (timeline gets the rest). */
  gridSharePercent: number;
};

const MIN_SHARE = 20;
const MAX_SHARE = 70;
const DEFAULT_SHARE = 42;

function storageKey(projectId: string): string {
  return `${STORAGE_PREFIX}${projectId}`;
}

export function clampGridSharePercent(pct: number): number {
  if (!Number.isFinite(pct)) return DEFAULT_SHARE;
  return Math.min(MAX_SHARE, Math.max(MIN_SHARE, pct));
}

export function loadGanttGridSplit(projectId: string): GanttGridSplit | null {
  if (!projectId || typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(storageKey(projectId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GanttGridSplit;
    if (!parsed || parsed.version !== 1 || typeof parsed.gridSharePercent !== 'number') {
      return null;
    }
    return {
      version: 1,
      gridSharePercent: clampGridSharePercent(parsed.gridSharePercent),
    };
  } catch {
    return null;
  }
}

export function saveGanttGridSplit(projectId: string, gridSharePercent: number): void {
  if (!projectId || typeof window === 'undefined') return;
  const next: GanttGridSplit = {
    version: 1,
    gridSharePercent: clampGridSharePercent(gridSharePercent),
  };
  try {
    window.sessionStorage.setItem(storageKey(projectId), JSON.stringify(next));
  } catch {
    // ignore quota / private mode
  }
}

export function gridSharePercentFromWidths(rootWidth: number, gridWidth: number): number {
  if (!(rootWidth > 0) || !(gridWidth > 0)) return DEFAULT_SHARE;
  return clampGridSharePercent((gridWidth / rootWidth) * 100);
}

export function gridWidthFromSharePercent(rootWidth: number, gridSharePercent: number): number {
  if (!(rootWidth > 0)) return 520;
  const pct = clampGridSharePercent(gridSharePercent);
  return Math.round(
    Math.min(Math.max((rootWidth * pct) / 100, 280), rootWidth * (MAX_SHARE / 100)),
  );
}

/** Resolve schedule grid width: session share % → fallback default (~42%). */
export function resolveScheduleGridWidth(
  rootWidth: number,
  projectId: string | null | undefined,
): number {
  if (!(rootWidth > 0)) return 520;
  const saved = projectId ? loadGanttGridSplit(projectId) : null;
  const pct = saved?.gridSharePercent ?? DEFAULT_SHARE;
  return gridWidthFromSharePercent(rootWidth, pct);
}
